
import { db, serverTimestamp } from "../../firebaseClient";
import { createLegalWorkLog } from "./legalWorkLogService";
import type { WorkLogModule } from "../types/legalWorkLog";

export type AutoTaskParams = {
  title: string;
  description?: string;
  module: string;
  refId?: string;
  assigneeName?: string; // Default to 'Kevin' or current user if not provided
  assigneeId?: string;   // Added for accurate log mapping
  dueDate?: string;
};

// Helper to map generic module string to WorkLogModule type
function mapToWorkLogModule(m: string): WorkLogModule {
  const map: Record<string, WorkLogModule> = {
    contract: "CONTRACT_SYSTEM",
    contractDraft: "CONTRACT_DRAFT",
    case: "DISPUTE", 
    ipProtection: "IP_PROTECTION",
    sealRequest: "STAMP",
    govCorrespondence: "PUBLIC_DOC",
    governance: "GOVERNANCE",
    template: "TEMPLATE_CENTER",
    BRAND: "BRAND",
    OTHER: "OTHER"
  };
  return map[m] || "OTHER";
}

export async function createAutoTask(params: AutoTaskParams) {
  try {
    const defaultAssignee = "Legal Team";
    const assigneeName = params.assigneeName || defaultAssignee;

    // 1. Create Task (Legacy task collection - kept for backward compat)
    // Even if we use work logs for the main view, keeping this ensures data integrity
    const taskPayload = {
      title: params.title,
      notes: (params.description || "") + (params.refId ? `\n(來源ID: ${params.refId})` : ""),
      sourceType: params.module,
      sourceId: params.refId || "",
      assignee: assigneeName, 
      status: "Pending",
      priority: "Medium",
      dueDate: params.dueDate || new Date().toISOString().split("T")[0],
      createdAt: serverTimestamp(),
      attachments: [],
      logId: null as string | null
    };

    const taskRef = await db.collection("legal_tasks").add(taskPayload);
    const taskId = taskRef.id;

    // 2. Create Linked Work Log (This is what appears in the unified "Pending Tasks" view)
    // We force creation even if assigneeId is missing (using a fallback) to ensure it appears in the list.
    const assigneeId = params.assigneeId || "group_legal";

    // IMPORTANT: To appear in "Pending Tasks", recordType MUST be 'todo' and status MUST be 'pending'
    const logId = await createLegalWorkLog({
        userId: assigneeId,
        userName: assigneeName,
        module: mapToWorkLogModule(params.module),
        recordType: "todo", // CRITICAL: This makes it appear in Pending Tasks
        action: "PENDING", 
        refId: params.refId || taskId,
        title: params.title,
        summary: params.description || "系統自動指派待辦",
        description: params.description || "系統自動指派待辦",
        finalStatus: "待處理",
        taskId: taskId,
        counterpartOrDept: "系統指派",
        status: "pending", // Ensure status matches pending logic
        refNo: params.refId || "" // Provide a safe fallback for refNo
    });

    if (logId) {
        await taskRef.update({ logId });
    }

    console.log("[AutoTask] Created:", params.title);
  } catch (e) {
    console.error("[AutoTask] Failed", e);
  }
}

/**
 * Marks tasks associated with a specific business object (sourceId) as completed.
 * Used when a Contract Review, Seal Request, or Case is closed/approved.
 */
export async function completeTaskBySource(sourceId: string, module?: string) {
  try {
    // Query for pending tasks linked to this sourceId
    const q = db.collection("legal_work_logs")
      .where("refId", "==", sourceId)
      .where("recordType", "==", "todo");
      // Note: We don't filter by status in query to ensure we catch all, 
      // but client-side check can be added if needed. 
      // Updating already done tasks to done is idempotent.

    const snapshot = await q.get();
    
    if (snapshot.empty) return;

    const batch = db.batch();
    let count = 0;

    snapshot.docs.forEach(doc => {
        const data = doc.data();
        // Only update if not already done
        if (data.status !== "done" && data.status !== "closed") {
            batch.update(doc.ref, {
                status: "done",
                finalStatus: "已完成", 
                todoStatus: "closed",
                updatedAt: serverTimestamp()
            });
            count++;
        }
    });
    
    if (count > 0) {
        await batch.commit();
        console.log(`[TaskService] Auto-completed ${count} tasks for source ${sourceId}`);
    }
  } catch(e) {
    console.error("[TaskService] Failed to complete tasks by source", e);
  }
}
